import * as Phaser from "phaser-ce"
import { COLOR } from "../../../common/Color"
import { log } from "../../../common/Logging"
import { FontType } from "../../../common/TextUtils"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { BackgroundSprite } from "../../generic-sprites/BackgroundSprite"
import {
    ButtonContainer,
    Buttons,
    ButtonType,
} from "../../generic-sprites/Buttons"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { RadioGroup, RadioOption } from "../../generic-sprites/RadioButtons"
import { Line, Rectangle } from "../../generic-sprites/Shape"
import { Audio } from "../../utils/Audio"
import { colorNum } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale, x, y } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Transitions } from "../transitions/Transitions"
import { loadAssets, MysteryAssets } from "./MysteryAssets"

type Quadrant = "UL" | "UR" | "LL" | "LR"

enum ActivityStates {
    START = "start",
    INSTRUCTIONS = "instructions",
    HUNT = "hunt",
    PUZZLE = "puzzle",
    SOLVE = "solve",
}

interface HuntItem {
    name: string
    sprite: Phaser.Sprite
    button: ButtonContainer
    buttonYPos: number
    quadrant: Quadrant
    clickedNumRef: number
    image: string
    finalHintX: number
    finalHintY: number
    finalHintScale: number
    foundRef: boolean
    foundX: number
    foundY: number
    foundScale: number
    foundTitle: string
    foundBody: string
    foundAudio: string
    playedFoundAudio: boolean
}

interface PuzzlePiece {
    name: string
    sprite: Phaser.Sprite
    image: string
    xCoord: number
    yCoord: number
    completeXCoord: number
    completeYCoord: number
}

export class MysteryState extends Phaser.State {
    private static readonly FONT = FontType.HAND_WRITING

    activityState: ActivityStates
    panelTextWrap = 0.6
    sidebarPanelTextWrap = 0.14
    sidebarTextPanelX: number
    sideBarXPos: number
    sideBarWidth: number
    textPanelX: number
    thinPanelLineWidth = 1
    thickPanelLineWidth = 4

    huntBackgroundGroup: Phaser.Group
    huntItemsGroup: Phaser.Group
    sidebarGroup: Phaser.Group
    sidebarInstructionsGroup: Phaser.Group
    sidebarHuntGroup: Phaser.Group
    panelGroup: Phaser.Group
    instructions1TextGroup: Phaser.Group
    instructions2TextGroup: Phaser.Group
    puzzleForegroundGroup: Phaser.Group
    puzzleInstructionsGroup: Phaser.Group
    puzzleGroup: Phaser.Group
    finalQuestionGroup: Phaser.Group

    hospital: Phaser.Sprite
    car: Phaser.Sprite
    obscuringScreen: Rectangle

    ecigarette: Phaser.Sprite | null = null
    inhaler: Phaser.Sprite | null = null
    ear: Phaser.Sprite | null = null
    heart: Phaser.Sprite | null = null
    lungs: Phaser.Sprite | null = null
    jaroftar: Phaser.Sprite | null = null
    oxygenmask: Phaser.Sprite | null = null
    stethoscope: Phaser.Sprite | null = null
    cloggedartery: Phaser.Sprite | null = null
    toddler: Phaser.Sprite | null = null

    foundHuntItemNextButton: ButtonContainer

    a1Piece: Phaser.Sprite | null = null
    a2Piece: Phaser.Sprite | null = null
    a3Piece: Phaser.Sprite | null = null
    a4Piece: Phaser.Sprite | null = null
    b1Piece: Phaser.Sprite | null = null
    b2Piece: Phaser.Sprite | null = null
    b3Piece: Phaser.Sprite | null = null
    b4Piece: Phaser.Sprite | null = null
    c1Piece: Phaser.Sprite | null = null
    c2Piece: Phaser.Sprite | null = null
    c3Piece: Phaser.Sprite | null = null
    c4Piece: Phaser.Sprite | null = null

    panel: Rectangle

    eCigButton: ButtonContainer | null = null
    inhalerButton: ButtonContainer | null = null
    earButton: ButtonContainer | null = null
    heartButton: ButtonContainer | null = null
    lungsButton: ButtonContainer | null = null
    tarButton: ButtonContainer | null = null
    maskButton: ButtonContainer | null = null
    stethoscopeButton: ButtonContainer | null = null
    arteryButton: ButtonContainer | null = null
    toddlerButton: ButtonContainer | null = null

    ecigHintClicked = 0
    inhalerHintClicked = 0
    earHintClicked = 0
    heartHintClicked = 0
    lungsHintClicked = 0
    tarHintClicked = 0
    maskHintClicked = 0
    stethoscopeHintClicked = 0
    arteryHintClicked = 0
    toddlerHintClicked = 0

    ecigFound = false
    inhalerFound = false
    earFound = false
    heartFound = false
    lungsFound = false
    tarFound = false
    maskFound = false
    stethoscopeFound = false
    arteryFound = false
    toddlerFound = false

    canClickHuntHintButtons = false
    canClickHuntItems = false

    canClickPuzzlePieces = false
    clickedPuzzlePiece: PuzzlePiece
    clickedPuzzlePieceHighlight: Rectangle

    huntItems: Array<HuntItem>
    initHuntItems = (
        x: (percent: number) => number,
        y: (percent: number) => number,
    ) => {
        this.huntItems = [
            {
                name: "E-cigarette",
                sprite: this.ecigarette,
                button: this.eCigButton,
                buttonYPos: y(0.25),
                quadrant: "UL",
                clickedNumRef: this.ecigHintClicked,
                image: MysteryAssets.ECIGARETTE,
                foundX: x(0.17),
                foundY: y(0.44),
                foundScale: 0.26,
                finalHintX: x(0.245),
                finalHintY: y(0.405),
                finalHintScale: 0.11,
                foundRef: this.ecigFound,
                foundTitle: "Have breathing problems",
                foundBody:
                    "Kayla is having trouble breathing. Breathing other people’s vapor can cause problems for anyone who inhales it.",
                foundAudio: MysteryAssets.CLUE_BREATHING_1,
                playedFoundAudio: false,
            },
            {
                name: "Inhaler",
                sprite: this.inhaler,
                button: this.inhalerButton,
                buttonYPos: y(0.3),
                quadrant: "LL",
                clickedNumRef: this.inhalerHintClicked,
                image: MysteryAssets.INHALER,
                finalHintX: x(0.14),
                finalHintY: y(0.58),
                finalHintScale: 0.13,
                foundX: x(0.168),
                foundY: y(0.435),
                foundScale: 0.3,
                foundRef: this.inhalerFound,
                foundTitle: "Get asthma",
                foundBody:
                    "Kayla is wheezing. Breathing in someone else’s vapor can trigger an asthma attack. When people have an asthma attack, they have " +
                    "trouble catching their breath.",
                foundAudio: MysteryAssets.CLUE_ASTHMA_1,
                playedFoundAudio: false,
            },
            {
                name: "Ear",
                sprite: this.ear,
                button: this.earButton,
                buttonYPos: y(0.35),
                quadrant: "UR",
                clickedNumRef: this.earHintClicked,
                image: MysteryAssets.EAR,
                finalHintX: x(0.492),
                finalHintY: y(0.042),
                finalHintScale: 0.15,
                foundX: x(0.171),
                foundY: y(0.414),
                foundScale: 0.34,
                foundRef: this.earFound,
                foundTitle: "Have ear infections",
                foundBody:
                    "When Kayla was a baby, she had lots of ear infections. Kids who live in homes where someone smokes get more ear infections.",
                foundAudio: MysteryAssets.CLUE_EAR,
                playedFoundAudio: false,
            },
            {
                name: "Heart",
                sprite: this.heart,
                button: this.heartButton,
                buttonYPos: y(0.4),
                quadrant: "LL",
                clickedNumRef: this.heartHintClicked,
                image: MysteryAssets.HEART,
                finalHintX: x(0.147),
                finalHintY: y(0.86),
                finalHintScale: 0.17,
                foundX: x(0.163),
                foundY: y(0.45),
                foundScale: 0.26,
                foundRef: this.heartFound,
                foundTitle: "Get heart disease",
                foundBody:
                    "Kayla is at risk for getting heart disease. Each year, thousands of people die from heart disease caused by inhaling other people's " +
                    "smoke.",
                foundAudio: MysteryAssets.CLUE_HEART_1,
                playedFoundAudio: false,
            },
            {
                name: "Lungs",
                sprite: this.lungs,
                button: this.lungsButton,
                buttonYPos: y(0.45),
                quadrant: "UL",
                clickedNumRef: this.lungsHintClicked,
                image: MysteryAssets.LUNGS,
                finalHintX: x(0.07),
                finalHintY: y(0.075),
                finalHintScale: 0.15,
                foundX: x(0.146),
                foundY: y(0.414),
                foundScale: 0.23,
                foundRef: this.lungsFound,
                foundTitle: "Get cancer",
                foundBody:
                    "Kayla can even get lung cancer. Every year, three thousand people die of lung cancer from breathing other people’s smoke.",
                foundAudio: MysteryAssets.CLUE_CANCER,
                playedFoundAudio: false,
            },
            {
                name: "Jar of Tar",
                sprite: this.jaroftar,
                button: this.tarButton,
                buttonYPos: y(0.5),
                quadrant: "UR",
                clickedNumRef: this.tarHintClicked,
                image: MysteryAssets.JAROFTAR,
                finalHintX: x(0.664),
                finalHintY: y(0.047),
                finalHintScale: 0.25,
                foundX: x(0.177),
                foundY: y(0.435),
                foundScale: 0.3,
                foundRef: this.tarFound,
                foundTitle: "Have breathing problems",
                foundBody:
                    "Kayla’s lungs have thick brown goop, called “tar,” in them. Smokers and people who breathe in other people’s smoke build up tar in " +
                    "their lungs, which makes it really hard to breathe.",
                foundAudio: MysteryAssets.CLUE_BREATHING_2,
                playedFoundAudio: false,
            },
            {
                name: "Oxygen mask",
                sprite: this.oxygenmask,
                button: this.maskButton,
                buttonYPos: y(0.55),
                quadrant: "LR",
                clickedNumRef: this.maskHintClicked,
                image: MysteryAssets.OXYGENMASK,
                finalHintX: x(0.697),
                finalHintY: y(0.493),
                finalHintScale: 0.2,
                foundX: x(0.141),
                foundY: y(0.435),
                foundScale: 0.25,
                foundRef: this.maskFound,
                foundTitle: "Get asthma",
                foundBody:
                    "Kayla has asthma attacks. Kids who breathe in other people’s vapor or cigarette smoke are more likely to have an asthma attack.",
                foundAudio: MysteryAssets.CLUE_ASTHMA_2,
                playedFoundAudio: false,
            },
            {
                name: "Stethoscope",
                sprite: this.stethoscope,
                button: this.stethoscopeButton,
                buttonYPos: y(0.6),
                quadrant: "LL",
                clickedNumRef: this.stethoscopeHintClicked,
                image: MysteryAssets.STETHOSCOPE,
                finalHintX: x(0.385),
                finalHintY: y(0.717),
                finalHintScale: 0.15,
                foundX: x(0.149),
                foundY: y(0.416),
                foundScale: 0.2,
                foundRef: this.stethoscopeFound,
                foundTitle: "Get lung infections",
                foundBody:
                    "Kayla went to the doctor because she had pneumonia. Pneumonia is a very serious lung infection. Kids who breathe in other people’s " +
                    "vapor are more likely to get lung infections.",
                foundAudio: MysteryAssets.CLUE_LUNG,
                playedFoundAudio: false,
            },
            {
                name: "Clogged Artery",
                sprite: this.cloggedartery,
                button: this.arteryButton,
                buttonYPos: y(0.65),
                quadrant: "UL",
                clickedNumRef: this.arteryHintClicked,
                image: MysteryAssets.CLOGGEDARTERY,
                finalHintX: x(0.348),
                finalHintY: y(0.271),
                finalHintScale: 0.155,
                foundX: x(0.152),
                foundY: y(0.416),
                foundScale: 0.35,
                foundRef: this.arteryFound,
                foundTitle: "Get heart disease",
                foundBody:
                    "A fatty substance called “plaque” is building up in Kayla’s arteries. Breathing other people’s smoke clogs the arteries that lead to " +
                    "the heart.",
                foundAudio: MysteryAssets.CLUE_HEART_2,
                playedFoundAudio: false,
            },
            {
                name: "Toddler",
                sprite: this.toddler,
                button: this.toddlerButton,
                buttonYPos: y(0.7),
                quadrant: "LL",
                clickedNumRef: this.toddlerHintClicked,
                image: MysteryAssets.TODDLER,
                finalHintX: x(0.022),
                finalHintY: y(0.457),
                finalHintScale: 0.16,
                foundX: x(0.131),
                foundY: y(0.405),
                foundScale: 0.2,
                foundRef: this.toddlerFound,
                foundTitle: "Get nicotine poisoning",
                foundBody:
                    "When she was little, Kayla had to go to the emergency room. She accidently drank some of her parent’s e-liquid because it tasted good" +
                    " and then she got really sick.",
                foundAudio: MysteryAssets.CLUE_NICOTINE,
                playedFoundAudio: false,
            },
        ]
    }

    puzzlePieces: Array<PuzzlePiece> = [
        {
            name: "A1",
            sprite: this.a1Piece,
            image: MysteryAssets.A1,
            xCoord: 0,
            yCoord: 2,
            completeXCoord: 0,
            completeYCoord: 0,
        },
        {
            name: "A2",
            sprite: this.a2Piece,
            image: MysteryAssets.A2,
            xCoord: 1,
            yCoord: 1,
            completeXCoord: 1,
            completeYCoord: 0,
        },
        {
            name: "A3",
            sprite: this.a3Piece,
            image: MysteryAssets.A3,
            xCoord: 1,
            yCoord: 2,
            completeXCoord: 2,
            completeYCoord: 0,
        },
        {
            name: "A4",
            sprite: this.a4Piece,
            image: MysteryAssets.A4,
            xCoord: 2,
            yCoord: 1,
            completeXCoord: 3,
            completeYCoord: 0,
        },
        {
            name: "B1",
            sprite: this.b1Piece,
            image: MysteryAssets.B1,
            xCoord: 3,
            yCoord: 0,
            completeXCoord: 0,
            completeYCoord: 1,
        },
        {
            name: "B2",
            sprite: this.b2Piece,
            image: MysteryAssets.B2,
            xCoord: 2,
            yCoord: 2,
            completeXCoord: 1,
            completeYCoord: 1,
        },
        {
            name: "B3",
            sprite: this.b3Piece,
            image: MysteryAssets.B3,
            xCoord: 2,
            yCoord: 0,
            completeXCoord: 2,
            completeYCoord: 1,
        },
        {
            name: "B4",
            sprite: this.b4Piece,
            image: MysteryAssets.B4,
            xCoord: 3,
            yCoord: 2,
            completeXCoord: 3,
            completeYCoord: 1,
        },
        {
            name: "C1",
            sprite: this.c1Piece,
            image: MysteryAssets.C1,
            xCoord: 1,
            yCoord: 0,
            completeXCoord: 0,
            completeYCoord: 2,
        },
        {
            name: "C2",
            sprite: this.c2Piece,
            image: MysteryAssets.C2,
            xCoord: 0,
            yCoord: 0,
            completeXCoord: 1,
            completeYCoord: 2,
        },
        {
            name: "C3",
            sprite: this.c3Piece,
            image: MysteryAssets.C3,
            xCoord: 0,
            yCoord: 1,
            completeXCoord: 2,
            completeYCoord: 2,
        },
        {
            name: "C4",
            sprite: this.c4Piece,
            image: MysteryAssets.C4,
            xCoord: 3,
            yCoord: 1,
            completeXCoord: 3,
            completeYCoord: 2,
        },
    ]

    preload() {
        loadAssets(this.game)
        loadGenericAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        if (!PhaserUtils.loggedIn()) {
            PhaserUtils.notLoggedInMessage(this.game)
            return
        }

        /////
        //
        // Set activityState to something other than ActivityStates.START to start at a different point
        // HUNT is not supported
        //
        /////
        this.activityState = ActivityStates.START
        // this.activityState = ActivityStates.INSTRUCTIONS
        // this.activityState = ActivityStates.PUZZLE
        // this.activityState = ActivityStates.SOLVE

        this.initHuntItems(x, y)
        this.sidebarTextPanelX = x(0.85)
        this.sideBarXPos = x(0.84)
        this.sideBarWidth = x(1) - this.sideBarXPos
        this.textPanelX = x(0.1)

        // Groups
        this.huntBackgroundGroup = this.game.add.group()
        this.sidebarGroup = this.game.add.group()
        this.sidebarInstructionsGroup = this.game.add.group()
        this.sidebarHuntGroup = this.game.add.group()
        this.huntItemsGroup = this.game.add.group()
        this.panelGroup = this.game.add.group()
        this.instructions1TextGroup = this.game.add.group()
        this.instructions2TextGroup = this.game.add.group()
        this.puzzleInstructionsGroup = this.game.add.group()
        this.puzzleGroup = this.game.add.group()
        this.puzzleForegroundGroup = this.game.add.group()
        this.finalQuestionGroup = this.game.add.group()

        // Background
        this.hospital = new BackgroundSprite(this.game, MysteryAssets.HOSPITAL)
        this.game.add.existing(this.hospital)

        this.huntBackgroundGroup.add(this.hospital)
        this.obscuringScreen = new Rectangle(
            this.game,
            x(0),
            y(0),
            this.sideBarXPos,
            y(1),
            {
                fillColor: colorNum.black,
                fillAlpha: 0.8,
                lineWidth: 0,
                group: this.huntBackgroundGroup,
            },
        )
        this.obscuringScreen.graphics.alpha = 0

        // Create Sidebar
        this.makeSidebarPanel(
            this.sideBarXPos,
            y(0),
            this.sideBarWidth,
            y(1),
            this.sidebarGroup,
        )

        // Hunt Items
        this.huntItems.forEach((item: HuntItem) => {
            item.sprite = Sprites.add(this.game, item.image, {
                x: item.finalHintX,
                y: item.finalHintY,
                scale: item.finalHintScale,
                group: this.huntItemsGroup,
            })
            item.sprite.alpha = 0
            item.sprite.inputEnabled = true
            item.sprite.events.onInputDown.add(() => {
                if (this.canClickHuntItems) {
                    this.foundHuntItem(item)
                    item.sprite.inputEnabled = false
                }
            })
        })

        this.progressToNextPart()
    }

    // Activity progression
    progressToNextPart = () => {
        switch (this.activityState) {
            case ActivityStates.START:
                this.activityState = ActivityStates.INSTRUCTIONS
                this.kaylaIntro()
                break
            case ActivityStates.INSTRUCTIONS:
                this.activityState = ActivityStates.HUNT
                this.showInstructions2()
                break
            case ActivityStates.HUNT:
                this.obscuringScreen.graphics.alpha = 0
                this.activityState = ActivityStates.PUZZLE
                this.beginHunt()
                break
            case ActivityStates.PUZZLE:
                this.activityState = ActivityStates.SOLVE
                this.endHunt()
                break
            case ActivityStates.SOLVE:
                Transitions.startOutro(this.game, ActivityKey.MYSTERY)
                break
            default:
                log.error("Unknown Mystery State")
        }
    }

    kaylaIntro = () => {
        this.makePanelText(
            "Kayla has grown up in a home where her parents smoke and vape. Today, Kayla is in the hospital because on the way to school, Kayla had trouble breathing and started gasping for air. Why did Kayla end up in the hospital?",
            this.sidebarTextPanelX,
            y(0.05),
            this.sidebarPanelTextWrap,
            this.sidebarInstructionsGroup,
            22,
        )

        Audio.add(this.game, MysteryAssets.INTRO_1, {
            onStop: () => {
                this.showInstructions1()
            },
        })
    }

    showInstructions1 = () => {
        this.game.add
            .tween(this.obscuringScreen.graphics)
            .to(
                { alpha: 1 },
                Phaser.Timer.SECOND / 4,
                Phaser.Easing.Linear.None,
                true,
            )
        this.panel = this.makePanel(
            x(0.08),
            y(0.15),
            x(0.65),
            y(0.7),
            this.panelGroup,
        )
        this.textPanelX = x(0.15)

        const buttonWidth = 0.15
        const buttonXPos = (this.sideBarXPos - buttonWidth) / 2
        const button = Buttons.create(
            this.game,
            "Next",
            this.progressToNextPart,
            {
                x: buttonXPos,
                y: y(0.75),
                width: buttonWidth,
                enabled: false,
                group: this.instructions1TextGroup,
                buttonType: ButtonType.PRIMARY,
            },
        )
        button.removeEvents()
        const intro2 = this.game.add.audio(MysteryAssets.INTRO_2)
        intro2.onStop.addOnce(() => {
            button.enable()
        })

        const timer = this.game.time.create()
        intro2.onPlay.addOnce(() => {
            timer.start()
        })

        timer.add(Phaser.Timer.SECOND * 2.5, () => {
            this.makePanelText(
                "1. Uncover clues in this picture.",
                this.textPanelX,
                y(0.29),
                this.panelTextWrap,
                this.instructions1TextGroup,
            )
        })

        timer.add(Phaser.Timer.SECOND * 11, () => {
            this.makePanelText(
                "2. Reasons why Kayla got sick.",
                this.textPanelX,
                y(0.39),
                this.panelTextWrap,
                this.instructions1TextGroup,
            )
        })

        timer.add(Phaser.Timer.SECOND * 15.5, () => {
            this.makePanelText(
                "3. Unscramble a puzzle.",
                this.textPanelX,
                y(0.49),
                this.panelTextWrap,
                this.instructions1TextGroup,
            )
        })

        timer.add(Phaser.Timer.SECOND * 19.5, () => {
            this.makePanelText(
                "4. Solve the mystery",
                this.textPanelX,
                y(0.59),
                this.panelTextWrap,
                this.instructions1TextGroup,
            )
        })
        intro2.play()

        this.makePanelText(
            "Can you solve this mystery?",
            this.textPanelX,
            y(0.2),
            this.panelTextWrap,
            this.instructions1TextGroup,
        )
    }

    showInstructions2 = () => {
        // this.panel should be created in showInstructions1. this is here in case a developer wants to skip instructions1
        // and jump into instructions2. During normal flow this.panel will always exist.
        if (!this.panel) {
            this.obscuringScreen.graphics.alpha = 1
            this.panel = this.makePanel(
                x(0.08),
                y(0.15),
                x(0.65),
                y(0.7),
                this.panelGroup,
            )
        }
        this.instructions1TextGroup.destroy()
        this.sidebarInstructionsGroup.destroy()

        // New sidebar items
        this.makePanelText(
            "Click on these objects in the picture.",
            this.sidebarTextPanelX,
            y(0.04),
            this.sidebarPanelTextWrap,
            this.sidebarHuntGroup,
            22,
        )

        this.huntItems.forEach((item, idx) => {
            item.button = Buttons.create(
                this.game,
                item.name,
                () => this.clickHint(item),
                {
                    x: this.sideBarXPos,
                    y: item.buttonYPos,
                    width: 0.16,
                    enabled: true,
                    group: this.sidebarHuntGroup,
                    buttonType: ButtonType.PRIMARY,
                },
            )
            new Line(
                this.game,
                this.sideBarXPos,
                item.buttonYPos,
                this.sideBarXPos + this.sideBarWidth,
                item.buttonYPos,
                { group: this.sidebarHuntGroup },
            )

            if (idx === this.huntItems.length - 1) {
                const bottomLineY = item.buttonYPos + item.button.button.height
                new Line(
                    this.game,
                    this.sideBarXPos,
                    bottomLineY,
                    this.sideBarXPos + this.sideBarWidth,
                    bottomLineY,
                    { group: this.sidebarHuntGroup },
                )
            }
        })

        this.makePanelText(
            "Click on the name of the object for hints.",
            this.sidebarTextPanelX,
            y(0.8),
            this.sidebarPanelTextWrap,
            this.sidebarHuntGroup,
            22,
        )

        // Add the disabled Next button
        const buttonWidth = 0.15
        const buttonXPos = (this.sideBarXPos - buttonWidth) / 2
        const button = Buttons.create(
            this.game,
            "Next",
            () => {
                this.canClickHuntItems = true
                this.progressToNextPart()
            },
            {
                x: buttonXPos,
                y: y(0.75),
                width: buttonWidth,
                enabled: false,
                group: this.instructions2TextGroup,
            },
        )

        // Add audio and enable the button after it plays
        const intro3 = this.game.add.audio(MysteryAssets.INTRO_3)
        intro3.onStop.addOnce(() => {
            button.enable()
        })

        const timer = this.game.time.create()
        intro3.onPlay.addOnce(() => {
            timer.start()
        })

        this.makePanelText(
            "· Find 10 objects.",
            this.textPanelX,
            y(0.28),
            this.panelTextWrap,
            this.instructions2TextGroup,
        )

        timer.add(Phaser.Timer.SECOND * 9, () => {
            this.makePanelText(
                "· Click on each object to see hidden message.",
                this.textPanelX,
                y(0.38),
                this.panelTextWrap,
                this.instructions2TextGroup,
            )
        })

        timer.add(Phaser.Timer.SECOND * 15, () => {
            this.makePanelText(
                "· You can get 3 hints for each object.",
                this.textPanelX,
                y(0.48),
                this.panelTextWrap,
                this.instructions2TextGroup,
            )
        })

        timer.add(Phaser.Timer.SECOND * 18, () => {
            this.makePanelText(
                "· Click on object’s name for hints",
                this.textPanelX,
                y(0.58),
                this.panelTextWrap,
                this.instructions2TextGroup,
            )
        })
        intro3.play()
    }

    beginHunt = () => {
        this.instructions2TextGroup.destroy()
        this.panelGroup.visible = false
        this.canClickHuntHintButtons = true
    }

    clickHint = (item: HuntItem) => {
        if (!this.canClickHuntHintButtons) {
            return
        }

        item.clickedNumRef += 1
        switch (item.clickedNumRef) {
            case 1:
                this.showFirstHint(item.quadrant)
                break
            case 2:
                this.showSecondHint(item)
                break
            default:
                this.showFinalHint(item)
        }
    }

    // Drawing functions
    makeSidebarPanel = (
        xPos: number,
        yPos: number,
        width: number,
        height: number,
        group?: Phaser.Group,
    ) => {
        return new Rectangle(this.game, xPos, yPos, width, height, {
            lineColor: colorNum.black,
            fillColor: colorNum.defaultButton,
            lineWidth: this.thinPanelLineWidth,
            group,
        })
    }

    makePanel = (
        xPos: number,
        yPos: number,
        width: number,
        height: number,
        group?: Phaser.Group,
    ) => {
        return new Rectangle(this.game, xPos, yPos, width, height, {
            lineColor: colorNum.black,
            fillColor: colorNum.white,
            cornerRounding: 20,
            lineWidth: this.thickPanelLineWidth,
            group,
        })
    }

    makePanelText = (
        text: string,
        xPos: number,
        yPos: number,
        wrapWidth: number,
        group?: Phaser.Group,
        fontPx?: number,
    ) => {
        const panelText = PhaserText.create(this.game, text, {
            x: xPos,
            y: yPos,
            alpha: 0,
            fontPx: fontPx ? fontPx : 28,
            wordWrapWidth: wrapWidth,
            group,
            type: MysteryState.FONT,
        })
        this.game.add
            .tween(panelText)
            .to(
                { alpha: 1 },
                Phaser.Timer.SECOND / 2,
                Phaser.Easing.Linear.None,
                true,
            )
        return panelText
    }

    showFirstHint = (quadrant: Quadrant) => {
        const redBoxGroup = this.game.add.group()
        switch (quadrant) {
            case "UL":
                this.showRedBox(0, 0, redBoxGroup)
                break
            case "UR":
                this.showRedBox(x(0.42), 0, redBoxGroup)
                break
            case "LL":
                this.showRedBox(0, y(0.5), redBoxGroup)
                break
            case "LR":
                this.showRedBox(x(0.42), y(0.5), redBoxGroup)
                break
            default:
                log.error("Unknown Mystery State")
        }

        this.canClickHuntHintButtons = false
        this.game.time.events.add(
            Phaser.Timer.SECOND,
            () => {
                redBoxGroup.destroy()
                this.canClickHuntHintButtons = true
            },
            this,
        )
    }

    showRedBox = (xPos: number, yPos: number, group: Phaser.Group) => {
        return new Rectangle(this.game, xPos, yPos, x(0.42), y(0.5), {
            fillColor: colorNum.red,
            fillAlpha: 0.4,
            lineWidth: 0,
            group,
        })
    }

    showHuntItemBox = (group: Phaser.Group) => {
        new Rectangle(this.game, x(0), y(0), this.sideBarXPos, y(1), {
            fillColor: colorNum.black,
            fillAlpha: 0.8,
            lineWidth: 0,
            group: group,
        })
        new Rectangle(this.game, x(0.08), y(0.15), x(0.65), y(0.7), {
            lineColor: colorNum.black,
            fillColor: colorNum.white,
            cornerRounding: 20,
            lineWidth: this.thickPanelLineWidth,
            group: group,
        })
    }

    showSecondHint = (item: HuntItem) => {
        const secondHintGroup: Phaser.Group = this.game.add.group()
        this.showHuntItemBox(secondHintGroup)
        const text = PhaserText.create(this.game, item.name, {
            y: y(0.25),
            fontPx: 25,
            group: secondHintGroup,
            type: MysteryState.FONT,
        })
        text.x = Math.floor(this.sideBarXPos / 2 - text.width / 2)
        const sprite = Sprites.add(this.game, item.image, {
            scale: item.foundScale,
        })
        sprite.x = Math.floor(this.sideBarXPos / 2 - sprite.width / 2)
        sprite.y = Math.floor(y(0.5) - sprite.width / 2)
        secondHintGroup.add(sprite)

        this.canClickHuntHintButtons = false
        this.game.time.events.add(
            Phaser.Timer.SECOND,
            () => {
                secondHintGroup.destroy()
                this.canClickHuntHintButtons = true
            },
            this,
        )
    }

    showFinalHint = (item: HuntItem) => {
        this.obscuringScreen.graphics.alpha = 1
        item.sprite.alpha = 1

        this.canClickHuntHintButtons = false
        this.game.time.events.add(
            Phaser.Timer.SECOND * 2,
            () => {
                this.obscuringScreen.graphics.alpha = 0
                item.sprite.alpha = 0
                this.canClickHuntHintButtons = true
            },
            this,
        )
    }

    foundHuntItem = (item: HuntItem) => {
        this.canClickHuntHintButtons = false
        this.canClickHuntItems = false

        // Mark item as found
        item.foundRef = true
        item.button.grayOut()

        // Show Hunt Item detail panel
        const foundHuntItemGroup: Phaser.Group = this.game.add.group()
        this.showHuntItemBox(foundHuntItemGroup)
        PhaserText.create(this.game, item.foundTitle, {
            x: x(0.3),
            y: y(0.3),
            wordWrapWidth: 0.4,
            fontPx: 25,
            group: foundHuntItemGroup,
            color: COLOR.red,
            type: MysteryState.FONT,
        })
        PhaserText.create(this.game, item.foundBody, {
            x: x(0.3),
            y: y(0.4),
            wordWrapWidth: 0.4,
            fontPx: 25,
            group: foundHuntItemGroup,
            type: MysteryState.FONT,
        })
        Sprites.add(this.game, item.image, {
            x: item.foundX,
            y: item.foundY,
            scale: item.foundScale,
            group: foundHuntItemGroup,
        })

        const buttonWidth = 0.15
        const buttonXPos = (this.sideBarXPos - buttonWidth) / 2
        this.foundHuntItemNextButton = Buttons.create(
            this.game,
            "Next",
            () => {
                foundHuntItemGroup.destroy()

                // If all items have been found, show the next section
                let foundAllHuntItems = true
                this.huntItems.forEach((huntItem: HuntItem) => {
                    if (!huntItem.foundRef) {
                        foundAllHuntItems = false
                    }
                })
                if (foundAllHuntItems) {
                    this.progressToNextPart()
                }
                this.canClickHuntHintButtons = true
                this.canClickHuntItems = true
            },
            {
                x: buttonXPos,
                y: y(0.75),
                width: buttonWidth,
                enabled: false,
                group: foundHuntItemGroup,
                buttonType: ButtonType.PRIMARY,
            },
        )

        // Play found item audio
        const foundItemAudio = Audio.add(this.game, item.foundAudio, {
            play: false,
            onStop: () => {
                this.foundHuntItemNextButton.enable()
                item.playedFoundAudio = true
            },
        })
        if (!item.playedFoundAudio) {
            this.foundHuntItemNextButton.disable()
            foundItemAudio.audio.play()
        }
    }

    endHunt = () => {
        Audio.add(this.game, MysteryAssets.ALRIGHT, {
            onStop: () => {
                this.obscuringScreen.graphics.alpha = 1
                Buttons.create(
                    this.game,
                    "Next",
                    () => {
                        this.sidebarInstructionsGroup.destroy()
                        this.sidebarHuntGroup.destroy()
                        this.huntItemsGroup.destroy()
                        this.huntBackgroundGroup.destroy()
                        this.startPuzzle()
                    },
                    {
                        x: x(0.65),
                        y: y(0.9),
                        enabled: true,
                        group: this.huntBackgroundGroup,
                        buttonType: ButtonType.PRIMARY,
                    },
                )
            },
        })
    }

    startPuzzle = () => {
        // Car hint image
        this.car = Sprites.add(this.game, MysteryAssets.CAR, {
            x: x(0),
            y: y(0),
            group: this.puzzleForegroundGroup,
        })
        const carScaleFactor = this.sideBarXPos / this.car.width
        Scale.scaleSprite(this.car, carScaleFactor)
        this.car.alpha = 0

        // Background
        new Rectangle(this.game, x(0), y(0), this.sideBarXPos, y(1), {
            fillColor: colorNum.black,
            lineWidth: 0,
            group: this.puzzleGroup,
        })

        // Place puzzle pieces
        const puzzlePieceDimension = this.sideBarXPos / 4
        this.puzzlePieces.forEach((piece: PuzzlePiece) => {
            piece.sprite = Sprites.add(this.game, piece.image, {
                x: piece.xCoord * puzzlePieceDimension,
                y: piece.yCoord * puzzlePieceDimension,
                group: this.puzzleGroup,
            })
            piece.sprite.width = puzzlePieceDimension
            piece.sprite.height = puzzlePieceDimension
            piece.sprite.inputEnabled = true
            // Click a puzzle piece
            piece.sprite.events.onInputDown.add(() => {
                if (this.canClickPuzzlePieces) {
                    if (this.clickedPuzzlePiece) {
                        this.swapPuzzlePieces(this.clickedPuzzlePiece, piece)
                        this.clickedPuzzlePiece = null
                        this.clickedPuzzlePieceHighlight.destroy()
                        if (this.puzzleComplete()) {
                            this.canClickPuzzlePieces = false
                            Audio.add(
                                this.game,
                                MysteryAssets.PIECES_TOGETHER,
                                {
                                    onStop: () => {
                                        this.endPuzzle()
                                    },
                                },
                            )
                        }
                    } else {
                        this.clickedPuzzlePiece = piece
                        this.clickedPuzzlePieceHighlight = new Rectangle(
                            this.game,
                            piece.xCoord * puzzlePieceDimension,
                            piece.yCoord * puzzlePieceDimension,
                            puzzlePieceDimension,
                            puzzlePieceDimension,
                            {
                                fillColor: colorNum.yellow,
                                fillAlpha: 0.4,
                                lineWidth: 2,
                                lineColor: colorNum.red,
                                group: this.puzzleGroup,
                            },
                        )
                    }
                }
            })
        })

        // Show puzzle instructions
        this.makePanelText(
            "Click on a puzzle piece and then click on the space where you want to move it.",
            this.sidebarTextPanelX,
            y(0.05),
            this.sidebarPanelTextWrap,
            this.puzzleInstructionsGroup,
            22,
        )
        this.makePanelText(
            "If you want to see what the completed puzzle looks like, click on the Hint button.",
            this.sidebarTextPanelX,
            y(0.4),
            this.sidebarPanelTextWrap,
            this.puzzleInstructionsGroup,
            22,
        )
        Buttons.create(this.game, "Hint", () => this.showPuzzleHint(), {
            x: this.sideBarXPos,
            y: y(0.75),
            width: 0.16,
            enabled: true,
            group: this.puzzleInstructionsGroup,
            buttonType: ButtonType.PRIMARY,
        })

        Audio.add(this.game, MysteryAssets.SOLVE, {
            onStop: () => (this.canClickPuzzlePieces = true),
        })
    }

    showPuzzleHint = () => {
        this.car.alpha = 1
        this.game.time.events.add(
            Phaser.Timer.SECOND,
            () => {
                this.car.alpha = 0
            },
            this,
        )
    }

    swapPuzzlePieces = (piece1: PuzzlePiece, piece2: PuzzlePiece) => {
        const puzzlePieceDimension = this.sideBarXPos / 4
        const piece1x = piece1.xCoord * puzzlePieceDimension
        const piece1y = piece1.yCoord * puzzlePieceDimension
        const piece2x = piece2.xCoord * puzzlePieceDimension
        const piece2y = piece2.yCoord * puzzlePieceDimension
        const piece1xCoord = piece1.xCoord
        const piece1yCoord = piece1.yCoord
        const piece2xCoord = piece2.xCoord
        const piece2yCoord = piece2.yCoord
        piece1.xCoord = piece2xCoord
        piece1.yCoord = piece2yCoord
        piece2.xCoord = piece1xCoord
        piece2.yCoord = piece1yCoord
        this.game.add
            .tween(piece1.sprite)
            .to(
                { x: piece2x },
                Phaser.Timer.SECOND / 4,
                Phaser.Easing.Cubic.Out,
                true,
            )
        this.game.add
            .tween(piece1.sprite)
            .to(
                { y: piece2y },
                Phaser.Timer.SECOND / 4,
                Phaser.Easing.Cubic.Out,
                true,
            )
        this.game.add
            .tween(piece2.sprite)
            .to(
                { x: piece1x },
                Phaser.Timer.SECOND / 4,
                Phaser.Easing.Cubic.Out,
                true,
            )
        this.game.add
            .tween(piece2.sprite)
            .to(
                { y: piece1y },
                Phaser.Timer.SECOND / 4,
                Phaser.Easing.Cubic.Out,
                true,
            )
    }

    logPieces = () => {
        this.puzzlePieces.forEach((piece: PuzzlePiece) => {
            log.info(piece.name, piece.xCoord, piece.yCoord)
        })
    }

    puzzleComplete = (): boolean => {
        let complete = true
        this.puzzlePieces.forEach((piece: PuzzlePiece) => {
            if (
                piece.xCoord !== piece.completeXCoord ||
                piece.yCoord !== piece.completeYCoord
            ) {
                complete = false
            }
        })
        return complete
    }

    endPuzzle = () => {
        Buttons.create(
            this.game,
            "Next",
            () => {
                this.puzzleGroup.destroy()
                this.puzzleInstructionsGroup.destroy()
                this.showQuestion()
            },
            {
                x: x(0.65),
                y: y(0.9),
                enabled: true,
                group: this.puzzleGroup,
                buttonType: ButtonType.PRIMARY,
            },
        )
    }

    showQuestion = () => {
        const finalQuestionQuestionContentGroup: Phaser.Group = this.game.add.group()
        new Rectangle(this.game, x(0), y(0), this.sideBarXPos, y(1), {
            fillColor: colorNum.black,
            fillAlpha: 0.8,
            lineWidth: 0,
            group: this.finalQuestionGroup,
        })
        new Rectangle(this.game, x(0.08), y(0.15), x(0.65), y(0.7), {
            lineColor: colorNum.black,
            fillColor: colorNum.white,
            cornerRounding: 20,
            lineWidth: this.thickPanelLineWidth,
            group: this.finalQuestionGroup,
        })
        PhaserText.create(
            this.game,
            "Which of these health effects linked to secondhand vapor or smoke caused Kayla to end up in the hospital?",
            {
                x: x(0.2),
                y: y(0.3),
                wordWrapWidth: 0.4,
                fontPx: 18,
                group: finalQuestionQuestionContentGroup,
            },
        )
        const options: Array<RadioOption> = [
            { text: "An ear infection" },
            { text: "Cancer" },
            { text: "An asthma attack" },
            { text: "Lung infection" },
            { text: "Heart disease" },
        ]
        const radioGroup = new RadioGroup(
            this.game,
            Scale.x(0.26),
            Scale.y(0.46),
            options,
            () => {
                // Narration audio?
                // const letter = String.fromCharCode("a".charCodeAt(0) + radioGroup.currentlySelectedIndex)
                // Audio.add(this.game, PersonalityAssets.findQuizAudio(questionNumber, letter as "a" | "b" | "c" | "d" | "e"))
                nextButton.enable(radioGroup.currentlySelected != null)
            },
        )
        finalQuestionQuestionContentGroup.add(radioGroup.group)

        const nextButton = Buttons.create(
            this.game,
            "Next",
            () => {
                const isCorrect =
                    radioGroup.currentlySelected === options[2].text
                finalQuestionQuestionContentGroup.destroy()
                this.showQuestionResult(isCorrect)
            },
            {
                x: x(0.4),
                y: y(0.75),
                enabled: false,
                group: finalQuestionQuestionContentGroup,
                buttonType: ButtonType.PRIMARY,
            },
        )

        // Play audio
        Audio.add(this.game, MysteryAssets.WHICH_EFFECTS)
    }

    showQuestionResult = (isCorrect: boolean) => {
        const finalQuestionResponseContentGroup: Phaser.Group = this.game.add.group()
        let audioKey: MysteryAssets
        let text: string
        if (isCorrect) {
            audioKey = MysteryAssets.OUTRO_1
            text =
                "Congratulations! You solved this mystery. Kayla had an asthma attack brought on by breathing in vapor and cigarette smoke while she was " +
                "in her family’s car. Breathing other people’s smoke or vapor can lead to many serious health problems. Click Next to continue."
        } else {
            audioKey = MysteryAssets.OUTRO_2
            text =
                "It’s true that breathing secondhand smoke or vapor can lead to many serious health problems, like ear infections, lung infections, heart " +
                "disease and cancer. But Kayla ended up in the hospital because she had a bad asthma attack brought on by breathing in smoke and vapor in her" +
                " family’s car. Click next to continue."
        }
        PhaserText.create(this.game, text, {
            x: x(0.2),
            y: y(0.25),
            wordWrapWidth: 0.4,
            fontPx: 25,
            group: finalQuestionResponseContentGroup,
            type: MysteryState.FONT,
        })
        const nextButton = Buttons.create(
            this.game,
            "Next",
            () => {
                this.progressToNextPart()
            },
            {
                x: x(0.4),
                y: y(0.75),
                enabled: false,
                group: finalQuestionResponseContentGroup,
                buttonType: ButtonType.PRIMARY,
            },
        )
        Audio.add(this.game, audioKey, {
            onStop: () => nextButton.enable(),
        })
    }
}
